/**
 * System Fields Configuration for Task Import
 *
 * Defines the standard task fields that can be mapped during import.
 * Custom fields from TaskBoard schema are handled separately.
 */

import { TaskFieldType } from '@Share/config/enums';

/**
 * Task system fields available for mapping.
 * These correspond to AppTask entity properties.
 * @type {Array<{key: string, displayName: string, required: boolean, description: string, dataType: string}>}
 */
export const TASK_SYSTEM_FIELDS = Object.freeze([
  {
    key: 'text',
    displayName: 'Task Title',
    required: true,
    description: 'The main title/name of the task (used as key for matching)',
    dataType: 'text',
  },
  {
    key: 'description',
    displayName: 'Description',
    required: false,
    description: 'Detailed description of the task',
    dataType: 'text',
  },
  {
    key: 'status',
    displayName: 'Status',
    required: false,
    description: 'Task status (matched by name to TaskBoard statuses)',
    dataType: 'text',
  },
  {
    key: 'progress',
    displayName: 'Progress',
    required: false,
    description: 'Task completion percentage (0-100)',
    dataType: 'number',
  },
  {
    key: 'dueDate',
    displayName: 'Due Date',
    required: false,
    description: 'Task deadline',
    dataType: 'date',
  },
  {
    key: 'startDate',
    displayName: 'Start Date',
    required: false,
    description: 'Task start date',
    dataType: 'date',
  },
  {
    key: 'priority',
    displayName: 'Priority',
    required: false,
    description: 'Task priority level',
    dataType: 'text',
  },
  {
    key: 'tags',
    displayName: 'Tags',
    required: false,
    description: 'Linked tags (comma-separated names)',
    dataType: 'text',
    icon: 'mdi-tag-multiple',
    placeholder: 'e.g., "Safety, Equipment, Inspection"',
  },
]);

/**
 * Get all system fields.
 * @returns {Array} Array of system field definitions
 */
export function getSystemFields() {
  return [...TASK_SYSTEM_FIELDS];
}

/**
 * Get only required system fields.
 * @returns {Array} Array of required field definitions
 */
export function getRequiredFields() {
  return TASK_SYSTEM_FIELDS.filter(f => f.required);
}

/**
 * Get a system field by key.
 * @param {string} key - The field key (e.g., 'text', 'description')
 * @returns {Object|undefined} Field definition or undefined
 */
export function getFieldByKey(key) {
  return TASK_SYSTEM_FIELDS.find(f => f.key === key);
}

/**
 * Check if a key is a system field.
 * @param {string} key - The field key to check
 * @returns {boolean} True if it's a system field
 */
export function isSystemField(key) {
  return TASK_SYSTEM_FIELDS.some(f => f.key === key);
}

/**
 * Data type options for custom fields (when creating new TaskBoard).
 * Uses TaskFieldType enum values.
 * Note: All TaskFieldType values are now supported including Users and Plan3D.
 */
export const FIELD_TYPE_OPTIONS = Object.freeze([
  { text: 'Text', value: TaskFieldType.Text, icon: 'mdi-format-text' },
  { text: 'Number', value: TaskFieldType.Number, icon: 'mdi-numeric' },
  { text: 'Date', value: TaskFieldType.Date, icon: 'mdi-calendar' },
  { text: 'Boolean', value: TaskFieldType.Boolean, icon: 'mdi-toggle-switch' },
  { text: 'Dropdown', value: TaskFieldType.Dropdown, icon: 'mdi-menu-down' },
  { text: 'Users', value: TaskFieldType.Users, icon: 'mdi-account-multiple' },
  { text: 'Plan3D', value: TaskFieldType.Plan3D, icon: 'mdi-palette' },
]);

/**
 * Import mode options.
 */
export const IMPORT_MODE_OPTIONS = Object.freeze([
  {
    value: 'existing',
    text: 'Existing TaskBoard',
    icon: 'mdi-clipboard-check-outline',
    description: 'Import tasks to an existing board',
  },
  {
    value: 'new',
    text: 'Create New TaskBoard',
    icon: 'mdi-clipboard-plus-outline',
    description: 'Create a new board with imported data',
  },
]);

/**
 * Scope type options (Facility or Facility Group - mutually exclusive).
 * Follows ForgeBaseTag pattern.
 */
export const SCOPE_TYPE_OPTIONS = Object.freeze([
  {
    value: 'Facility',
    text: 'Facility',
    icon: 'mdi-domain',
    description: 'Link to a single facility',
  },
  {
    value: 'FacilityGroup',
    text: 'Facility Group',
    icon: 'mdi-folder-multiple',
    description: 'Share across facility group',
  },
]);

/**
 * Duplicate handling options.
 * Note: Per user requirement, auto-overwrite is the default behavior.
 */
export const DUPLICATE_HANDLING_OPTIONS = Object.freeze([
  {
    value: 'Overwrite',
    text: 'Overwrite',
    description: 'Update existing tasks with new data (default)',
  },
  {
    value: 'Skip',
    text: 'Skip',
    description: 'Skip rows that match existing tasks',
  },
]);

/**
 * Detect field type from sample values.
 * Used when creating new TaskBoard to suggest field types.
 * @param {Array} values - Array of sample values from Excel column
 * @returns {number} TaskFieldType value
 */
export function detectFieldType(values) {
  if (!values || values.length === 0) {
    return TaskFieldType.Text;
  }

  // Filter out empty values
  const nonEmpty = values.filter(v => v !== null && v !== undefined && String(v).trim() !== '');
  if (nonEmpty.length === 0) {
    return TaskFieldType.Text;
  }

  // Check for date pattern
  const datePattern = /^\d{1,2}[/-]\d{1,2}[/-]\d{2,4}$/;
  const isoDatePattern = /^\d{4}-\d{2}-\d{2}/;
  const allDates = nonEmpty.every(v => datePattern.test(String(v)) || isoDatePattern.test(String(v)));
  if (allDates) {
    return TaskFieldType.Date;
  }

  // Check for email pattern (Users field)
  // Matches standard email format: user@domain.tld
  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const allEmails = nonEmpty.every(v => emailPattern.test(String(v).trim()));
  if (allEmails) {
    return TaskFieldType.Users;
  }

  // Check for boolean
  const booleanValues = ['yes', 'no', 'true', 'false', '1', '0', 'y', 'n'];
  const allBooleans = nonEmpty.every(v => booleanValues.includes(String(v).toLowerCase()));
  if (allBooleans) {
    return TaskFieldType.Boolean;
  }

  // Check for number
  const allNumbers = nonEmpty.every(v => !isNaN(parseFloat(v)) && isFinite(v));
  if (allNumbers) {
    return TaskFieldType.Number;
  }

  // Check for dropdown (limited unique values)
  const uniqueValues = [...new Set(nonEmpty.map(v => String(v).trim()))];
  if (uniqueValues.length <= 10 && uniqueValues.length < nonEmpty.length * 0.3) {
    return TaskFieldType.Dropdown;
  }

  // Default to text
  return TaskFieldType.Text;
}

/**
 * Get dropdown options from sample values.
 * @param {Array} values - Array of sample values
 * @returns {Array} Unique string values for dropdown options
 */
export function extractDropdownOptions(values) {
  if (!values || values.length === 0) return [];

  const uniqueValues = [...new Set(
    values
      .filter(v => v !== null && v !== undefined && String(v).trim() !== '')
      .map(v => String(v).trim())
  )];

  return uniqueValues.sort();
}

export default {
  TASK_SYSTEM_FIELDS,
  FIELD_TYPE_OPTIONS,
  IMPORT_MODE_OPTIONS,
  SCOPE_TYPE_OPTIONS,
  DUPLICATE_HANDLING_OPTIONS,
  getSystemFields,
  getRequiredFields,
  getFieldByKey,
  isSystemField,
  detectFieldType,
  extractDropdownOptions,
};
